import type { ReactElement } from "react";
import { useQuery } from "@tanstack/react-query";
import { cn } from "@/lib/cn";
import { marketApi } from "@/lib/marketApi";
import { formatOi, formatSignedOi } from "@/lib/oiFormat";
import type { MarketIndex, Moneyness, OiEntry, OptionsResponse } from "@/types/market";

const POLL_MS = 30_000;

/** Per-side strike → color maps built by OptionsTab from the oi-drift
 * series, so a change-ladder bar matches its trend line's color. */
export interface StrikeColorMaps {
  call: ReadonlyMap<number, string>;
  put: ReadonlyMap<number, string>;
}

const NO_COLORS: StrikeColorMaps = { call: new Map(), put: new Map() };

export function OptionsPanel({
  index,
  strikeColors = NO_COLORS,
}: Readonly<{ index: MarketIndex; strikeColors?: StrikeColorMaps }>): ReactElement {
  const options = useQuery({
    queryKey: ["market", "options", index],
    queryFn: () => marketApi.options(index),
    refetchInterval: POLL_MS,
  });
  const data = options.data?.connected ? options.data : null;
  // The live surfaces (ladders, max pain chip, VIX) need a Fyers session;
  // the history charts render below without one (same tab since 2026-07-27).
  return (
    <section className="card mb-4">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <h2>Options</h2>
        {data !== null && <StatStrip data={data} />}
      </div>
      {data !== null ? (
        <OptionsLadders data={data} strikeColors={strikeColors} />
      ) : (
        <div className="text-xs text-ink-muted">
          Live OI ladders appear after the Fyers login (market hours).
        </div>
      )}
    </section>
  );
}

function OptionsLadders({
  data,
  strikeColors,
}: Readonly<{ data: OptionsResponse; strikeColors: StrikeColorMaps }>): ReactElement {
  // Bars in a call/put pair share one scale so their lengths are directly
  // comparable — otherwise each column normalises to its own max and a
  // 76 L put bar can render longer than a 2 Cr call bar.
  const oiMax = maxAbsValue([...data.top_call_oi, ...data.top_put_oi]);
  const changeMax = maxAbsValue([...data.top_call_oi_change, ...data.top_put_oi_change]);
  // One row of four on wide screens: keeps the card short enough that the
  // drift charts below stay visible without scrolling (operator request
  // 2026-07-27).
  return (
    <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
      <OiLadder
        title="Top Call OI"
        entries={data.top_call_oi}
        maxAbs={oiMax}
        tone="call"
        signed={false}
      />
      <OiLadder
        title="Top Put OI"
        entries={data.top_put_oi}
        maxAbs={oiMax}
        tone="put"
        signed={false}
      />
      <OiLadder
        title="Call OI Change"
        entries={data.top_call_oi_change}
        maxAbs={changeMax}
        tone="call"
        signed={true}
        colors={strikeColors.call}
      />
      <OiLadder
        title="Put OI Change"
        entries={data.top_put_oi_change}
        maxAbs={changeMax}
        tone="put"
        signed={true}
        colors={strikeColors.put}
      />
    </div>
  );
}

function maxAbsValue(entries: OiEntry[]): number {
  return Math.max(...entries.map((e) => Math.abs(e.value)), 1);
}

function StatStrip({ data }: Readonly<{ data: OptionsResponse }>): ReactElement {
  const vixUp = (data.vix_change_pct ?? 0) >= 0;
  return (
    <div className="flex flex-wrap items-center gap-2">
      {data.max_pain !== null && <MaxPainChip maxPain={data.max_pain} spot={data.spot} />}
      {data.vix_last !== null && (
        <span
          className={cn("pill", vixUp ? "bg-danger-bg text-danger" : "bg-success-bg text-success")}
        >
          India VIX {data.vix_last.toFixed(2)}
          {data.vix_change_pct !== null && ` (${formatSigned(data.vix_change_pct)}%)`}
        </span>
      )}
      {data.pcr !== null && (
        <span
          className={cn(
            "pill",
            data.pcr >= 1 ? "bg-success-bg text-success" : "bg-danger-bg text-danger",
          )}
          title="Put-Call Ratio: total put OI / total call OI (nearest expiry)"
        >
          PCR {data.pcr.toFixed(2)}
        </span>
      )}
      {data.expiry !== null && (
        <span className="pill bg-surface-2 text-ink-secondary">Expiry {data.expiry}</span>
      )}
    </div>
  );
}

function MaxPainChip({
  maxPain,
  spot,
}: Readonly<{
  maxPain: number;
  spot: number | null;
}>): ReactElement {
  // Arrow = where max pain sits RELATIVE TO SPOT: above spot (green up)
  // suggests an upward expiry pull, below spot (red down) the reverse.
  const above = spot !== null && maxPain > spot;
  const below = spot !== null && maxPain < spot;
  return (
    <span
      className="pill bg-accent-bg text-accent"
      title="Arrow = max pain vs spot (above spot pulls up, below pulls down)"
    >
      Max Pain {maxPain.toLocaleString("en-IN")}
      {above && <span className="text-success">▲</span>}
      {below && <span className="text-danger">▼</span>}
    </span>
  );
}

function OiLadder({
  title,
  entries,
  maxAbs,
  tone,
  signed,
  colors,
}: Readonly<{
  title: string;
  entries: OiEntry[];
  maxAbs: number;
  tone: "call" | "put";
  signed: boolean;
  /** Change ladders only: strike → trend-line color (shared palette). */
  colors?: ReadonlyMap<number, string>;
}>): ReactElement {
  // Net for the signed change ladders; plain sum for the OI ladders.
  // ONLY the top-3 rows count: the positional strikes ride along for
  // context, and including them would make the figure swing with how many
  // of them happened to fall outside the top 3 — two ladders would stop
  // being comparable.
  const total = entries.filter((e) => e.top_ranked).reduce((a, e) => a + e.value, 0);
  return (
    <div>
      <h3
        className="mb-1.5 text-sm text-ink-secondary"
        title="Top 3 strikes plus the ATM / ITM-1 / OTM-1 strikes (deduplicated, so 3-6 rows)"
      >
        {title}
      </h3>
      {entries.length === 0 && <div className="text-xs text-ink-muted">No data</div>}
      <div className="space-y-1">
        {entries.map((e) => (
          <OiBar
            key={e.strike}
            entry={e}
            maxAbs={maxAbs}
            tone={tone}
            signed={signed}
            color={colors?.get(e.strike)}
          />
        ))}
      </div>
      {entries.length > 0 && <LadderTotal total={total} signed={signed} />}
    </div>
  );
}

function LadderTotal({
  total,
  signed,
}: Readonly<{ total: number; signed: boolean }>): ReactElement {
  return (
    <div className="mt-1 flex items-center gap-2 border-t border-line pt-1 text-xs font-medium">
      <span
        className="w-14 shrink-0"
        title="Sum of the top 3 strikes by OI — ATM/ITM/OTM rows are shown for context and are not counted unless they are also top 3"
      >
        Top 3
      </span>
      <span className="flex-1" />
      <span className="w-16 shrink-0 text-right font-mono">
        {signed ? formatSignedOi(total) : formatOi(total)}
      </span>
    </div>
  );
}

function OiBar({
  entry,
  maxAbs,
  tone,
  signed,
  color,
}: Readonly<{
  entry: OiEntry;
  maxAbs: number;
  tone: "call" | "put";
  signed: boolean;
  /** Trend-line color for this strike; falls back to the side tone. */
  color?: string | undefined;
}>): ReactElement {
  const pct = Math.max((Math.abs(entry.value) / maxAbs) * 100, 2);
  const unwinding = signed && entry.value < 0;
  const width = `${String(pct)}%`;
  return (
    <div className="flex items-center gap-2 text-xs">
      <span className="w-14 shrink-0 font-mono">{entry.strike.toLocaleString("en-IN")}</span>
      <MoneynessTag moneyness={entry.moneyness} />
      <div className="h-2.5 flex-1 overflow-hidden rounded-full bg-surface-2">
        <div
          className={cn(
            "h-full rounded-full",
            color === undefined && (tone === "call" ? "bg-danger-fill" : "bg-success-fill"),
            unwinding && "opacity-40",
          )}
          style={color === undefined ? { width } : { width, backgroundColor: color }}
        />
      </div>
      <span className="w-16 shrink-0 text-right font-mono">
        {signed ? formatSignedOi(entry.value) : formatOi(entry.value)}
      </span>
    </div>
  );
}

const MONEYNESS_TITLE: Record<Moneyness, string> = {
  ATM: "At the money — strike nearest spot",
  ITM1: "One strike in the money (side-relative)",
  OTM1: "One strike out of the money (side-relative)",
};

function MoneynessTag({ moneyness }: Readonly<{ moneyness: Moneyness | null }>): ReactElement {
  // Fixed-width slot either way, so bars stay aligned down the ladder
  // whether or not a given row carries a tag.
  if (moneyness === null) return <span className="w-9 shrink-0" aria-hidden="true" />;
  return (
    <span
      className={cn(
        "w-9 shrink-0 rounded px-1 text-center text-[10px] font-medium leading-4",
        moneyness === "ATM" ? "bg-accent-bg text-accent" : "bg-surface-2 text-ink-secondary",
      )}
      title={MONEYNESS_TITLE[moneyness]}
    >
      {moneyness}
    </span>
  );
}

function formatSigned(value: number): string {
  return `${value < 0 ? "" : "+"}${value.toFixed(2)}`;
}
